import React, { useState } from "react";
import { motion } from "framer-motion";
import { Download, Mail, ArrowUpRight, Check, AlertCircle, Target, FileBarChart } from "lucide-react";
import { toast } from "sonner";
import TopBar from "../components/layout/TopBar";
import { Movement } from "../components/Indicators";
import useApi from "../hooks/useApi";
import { useBranch } from "../context/BranchContext";

function Metric({ label, from, to, suffix = "", change }) {
  return (
    <div className="card p-5">
      <span className="label-caps">{label}</span>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="text-ink-3 text-lg font-semibold">{from}{suffix}</span>
        <ArrowUpRight size={16} className="text-ink-3" />
        <span className="text-3xl font-extrabold tracking-tighter text-ink">{to}{suffix}</span>
      </div>
      {change != null && <div className="mt-1"><Movement value={change} /></div>}
    </div>
  );
}

function Section({ icon: Icon, title, items, color }) {
  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={17} strokeWidth={1.75} style={{ color }} />
        <h3 className="font-bold tracking-tight text-ink">{title}</h3>
      </div>
      <ul className="space-y-2">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-ink-2 leading-relaxed">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Reports() {
  const { branchId } = useBranch();
  const { data } = useApi(`/reports?branch_id=${branchId}`, [branchId]);
  const [activeId, setActiveId] = useState(null);
  const reports = data || [];
  const report = reports.find((r) => r.id === activeId) || reports[0];

  const downloadPdf = () => {
    if (!report) return;
    window.print();
    toast.success("Opening print / save-as-PDF");
  };

  if (!report) return <><TopBar title="Reports" /><div className="p-8 animate-pulse"><div className="h-72 rounded-xl" style={{ background: "#EDEEE9" }} /></div></>;

  return (
    <>
      <TopBar title="Reports" subtitle="Premium monthly Growth Reports." />
      <div className="p-6 sm:p-8 max-w-[1200px]">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {reports.map((r) => (
            <button key={r.id} onClick={() => setActiveId(r.id)} data-testid={`report-tab-${r.id}`}
              className="rounded-full px-3.5 py-1.5 text-sm font-medium transition-all border"
              style={report.id === r.id ? { background: "#151515", color: "#fff", borderColor: "#151515" } : { background: "#fff", color: "#6B6B6B", borderColor: "#E7E8E4" }}>
              {r.month} {r.year}
            </button>
          ))}
        </div>

        <motion.div key={report.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
            <div>
              <p className="label-caps">Growth Report</p>
              <h1 className="text-4xl font-extrabold tracking-tighter text-ink mt-1">{report.month} {report.year}</h1>
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary focus-ring" onClick={() => toast.success("Report emailed to your team")} data-testid="email-report"><Mail size={15} strokeWidth={1.75} /> Email report</button>
              <button className="btn-primary focus-ring" onClick={downloadPdf} data-testid="download-pdf"><Download size={15} strokeWidth={2} /> Download PDF</button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Metric label="Growth Score" from={report.score_from} to={report.score_to} change={report.score_to - report.score_from} />
            <Metric label="Local ranking" from={`#${report.rank_from}`} to={`#${report.rank_to}`} />
            <Metric label="Content utilisation" from={report.utilisation_from} to={report.utilisation_to} suffix="%" change={report.utilisation_to - report.utilisation_from} />
            <div className="card p-5"><span className="label-caps">Social reach</span><div className="text-3xl font-extrabold tracking-tighter mt-2" style={{ color: "#678A16" }}>+{report.reach_change}%</div></div>
            <div className="card p-5"><span className="label-caps">Engagement</span><div className="text-3xl font-extrabold tracking-tighter mt-2" style={{ color: "#678A16" }}>+{report.engagement_change}%</div></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section icon={Check} title="What worked" items={report.worked} color="#678A16" />
            <Section icon={ArrowUpRight} title="What improved" items={report.improved} color="#93C020" />
            <Section icon={AlertCircle} title="Missed opportunities" items={report.missed} color="#E5A93D" />
            <Section icon={Target} title={`Recommended focus for ${report.month === "July" ? "August" : "next month"}`} items={report.focus} color="#151515" />
          </div>
        </motion.div>
      </div>
    </>
  );
}
